import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  LifecycleStatus,
  StreakPolicy,
  StreakMilestone,
  gamificationApi,
} from "api/admin/gamification";
import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionCard,
  StatusPill,
  errorMessage,
  fieldClass,
  formatDate,
  formatLabel,
} from "./components";

type MilestoneForm = {
  name: string;
  streakCount: string;
  eloPoints: string;
  rewardDefinitionIds: string[];
  status: LifecycleStatus;
  reason: string;
};

const milestoneForm = (milestone?: StreakMilestone): MilestoneForm => ({
  name: milestone?.name ?? "",
  streakCount: String(milestone?.streakCount ?? milestone?.weeks ?? ""),
  eloPoints: String(milestone?.eloPoints ?? ""),
  rewardDefinitionIds: (milestone?.rewardDefinitionIds ??
    (milestone?.rewardDefinitionId ? [milestone.rewardDefinitionId] : [])).map(String),
  status: milestone?.status ?? "active",
  reason: "",
});

export default function Streaks() {
  const { can } = useAdminAccess();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<StreakMilestone | null | undefined>();
  const [form, setForm] = useState<MilestoneForm>(milestoneForm());
  const [page, setPage] = useState(1);
  const [userFilter, setUserFilter] = useState("");
  const [editingDecay, setEditingDecay] = useState(false);
  const [decayPoints, setDecayPoints] = useState("");
  const [decayReason, setDecayReason] = useState("");
  const [decayEffectiveFrom, setDecayEffectiveFrom] = useState("");
  const [editingMultiplier, setEditingMultiplier] = useState(false);
  const [multiplierReason, setMultiplierReason] = useState("");
  const [multiplierEffectiveFrom, setMultiplierEffectiveFrom] = useState("");
  const [tierMultipliers, setTierMultipliers] = useState<Record<string, string>>({});

  const milestones = useQuery(
    "gamification-streak-milestones",
    gamificationApi.listMilestones,
    { enabled: can("gamification.streaks.view"), retry: false },
  );
  const rewards = useQuery(
    "gamification-reward-definitions",
    gamificationApi.listRewardDefinitions,
    { enabled: can("gamification.rewardDefinitions.view"), retry: false },
  );
  const events = useQuery(
    ["gamification-streak-events", page, userFilter],
    () =>
      gamificationApi.listStreakEvents({
        page,
        limit: 20,
        userId: userFilter ? Number(userFilter) : undefined,
      }),
    { retry: false, keepPreviousData: true },
  );
  const policy = useQuery(
    "gamification-streak-policy",
    gamificationApi.getStreakPolicy,
    { enabled: can("gamification.streaks.view"), retry: false },
  );
  const tiers = useQuery("gamification-tiers", gamificationApi.listTiers, { enabled: can("gamification.eloTiers.view"),
    retry: false,
  });

  const save = useMutation(
    async () => {
      const input = {
        name: form.name.trim(),
        streakCount: Number(form.streakCount),
        eloPoints: Number(form.eloPoints),
        rewardDefinitionIds: form.rewardDefinitionIds.map(Number),
        status: form.status,
        reason: form.reason || undefined,
      };
      return editing
        ? gamificationApi.updateMilestone(editing.id, input)
        : gamificationApi.createMilestone(input);
    },
    {
      onSuccess: () => {
        toast.success(editing ? "Milestone updated" : "Milestone created");
        queryClient.invalidateQueries("gamification-streak-milestones");
        setEditing(undefined);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );

  const open = (milestone: StreakMilestone | null) => {
    setEditing(milestone);
    setForm(milestoneForm(milestone ?? undefined));
  };
  const openDecayEditor = () => {
    const currentPolicy = policy.data?.policy;
    setDecayPoints(String(currentPolicy?.decayPoints ?? 5));
    setDecayReason("");
    setDecayEffectiveFrom(new Date().toISOString().slice(0, 16));
    setEditingDecay(true);
  };
  const openMultiplierEditor = () => {
    const currentPolicy = policy.data?.policy;
    setMultiplierReason("");
    setMultiplierEffectiveFrom(new Date().toISOString().slice(0, 16));
    setTierMultipliers(
      Object.fromEntries(
        (tiers.data?.tiers ?? []).map((tier) => [
          tier.code,
          String(
            currentPolicy?.tierMultipliers?.[tier.code] ??
              currentPolicy?.tierMultipliers?.[tier.code.toLowerCase()] ??
              1,
          ),
        ]),
      ),
    );
    setEditingMultiplier(true);
  };
  const saveDecay = useMutation(
    () => {
      const currentPolicy: StreakPolicy = policy.data?.policy ?? {
        windowHours: 168,
        standingAtRiskHours: 72,
        decayPoints: 5,
        tierMultipliers: {},
      };
      return gamificationApi.createStreakPolicy({
        market: currentPolicy.market ?? null,
        status: "active",
        effectiveFrom: new Date(decayEffectiveFrom).toISOString(),
        effectiveTo: null,
        windowHours:
          currentPolicy.windowHours ??
          Math.round((currentPolicy.windowMilliseconds ?? 168 * 3_600_000) / 3_600_000),
        standingAtRiskHours:
          currentPolicy.standingAtRiskHours ??
          Math.round((currentPolicy.standingAtRiskWindowMilliseconds ?? 72 * 3_600_000) / 3_600_000),
        decayPoints: Number(decayPoints),
        tierMultipliers: currentPolicy.tierMultipliers,
        reason: decayReason.trim(),
      });
    },
    {
      onSuccess: () => {
        toast.success("ELO decay policy updated");
        queryClient.invalidateQueries("gamification-streak-policy");
        setEditingDecay(false);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );
  const saveMultiplier = useMutation(
    () => {
      const currentPolicy: StreakPolicy = policy.data?.policy ?? {
        windowHours: 168,
        standingAtRiskHours: 72,
        decayPoints: 5,
        tierMultipliers: {},
      };
      return gamificationApi.createStreakPolicy({
        market: currentPolicy.market ?? null,
        status: "active",
        effectiveFrom: new Date(multiplierEffectiveFrom).toISOString(),
        effectiveTo: null,
        windowHours:
          currentPolicy.windowHours ??
          Math.round((currentPolicy.windowMilliseconds ?? 168 * 3_600_000) / 3_600_000),
        standingAtRiskHours:
          currentPolicy.standingAtRiskHours ??
          Math.round((currentPolicy.standingAtRiskWindowMilliseconds ?? 72 * 3_600_000) / 3_600_000),
        decayPoints: currentPolicy.decayPoints,
        tierMultipliers: {
          ...currentPolicy.tierMultipliers,
          ...Object.fromEntries(
            Object.entries(tierMultipliers).map(([tierCode, multiplier]) => [
              tierCode,
              Number(multiplier),
            ]),
          ),
        },
        reason: multiplierReason.trim(),
      });
    },
    {
      onSuccess: () => {
        toast.success("Active-streak multipliers updated");
        queryClient.invalidateQueries("gamification-streak-policy");
        setEditingMultiplier(false);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );
  const rewardNames = (milestone: StreakMilestone) =>
    (milestone.rewardDefinitionIds ??
      (milestone.rewardDefinitionId ? [milestone.rewardDefinitionId] : []))
      .map((id) => rewards.data?.rewards.find((reward) => reward.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "No rewards";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="How the weekly streak works"
        description="The week runs Monday to Sunday in Africa/Lagos time."
      >
        <div className="grid gap-px bg-gray-100 md:grid-cols-4">
          <RuleStep
            label="Monday"
            title="A new week starts"
            body="The user can qualify or use their monthly shield."
          />
          <RuleStep
            label="First qualifying action"
            title="Streak increases by one"
            body="Only a revenue-generating action counts, once per week."
            active
          />
          <RuleStep
            label="Friday"
            title="Warning starts"
            body="Users who have not qualified are warned before the week closes."
          />
          <RuleStep
            label="Sunday"
            title="Unprotected streak resets"
            body="A shield preserves the count but does not increase it."
          />
        </div>
        <div className="flex flex-col gap-2 border-t border-gray-100 bg-orange-50 px-5 py-4 text-sm text-orange-950 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-semibold">Monthly streak shield</span>
          <span>1 per user · manual use · expires at month-end · never accumulates</span>
        </div>
      </SectionCard>

      <SectionCard
        title="ELO decay after a missed streak"
        description="This applies once when a streak week closes without a qualifying action or a manually used shield. It never reduces a user's ELO below zero."
        action={<AdminPermission permission={"gamification.streaks.create"}>
          <Button
            variant="secondary"
            disabled={policy.isLoading || Boolean(policy.error)}
            onClick={openDecayEditor}
          >
            Edit ELO decay
          </Button>
        </AdminPermission>}
      >
        {policy.isLoading ? (
          <LoadingState rows={1} />
        ) : policy.error ? (
          <ErrorState error={policy.error} onRetry={() => policy.refetch()} />
        ) : (
          <p className="py-3 text-2xl font-semibold text-gray-950">
            {policy.data?.policy?.decayPoints ?? 5} ELO points
          </p>
        )}
      </SectionCard>

      <AdminPermission permission={"gamification.eloTiers.view"}>
        <SectionCard
          title="Active-streak ELO multiplier"
          description="Apply a tier-based bonus to Base ELO Points only when the member has an active streak."
          action={
            <AdminPermission permission={"gamification.streaks.create"}>
              <Button
                variant="secondary"
                disabled={
                  policy.isLoading ||
                  tiers.isLoading ||
                  Boolean(policy.error) ||
                  Boolean(tiers.error)
                }
                onClick={openMultiplierEditor}
              >
                Edit multipliers
              </Button>
            </AdminPermission>
          }
        >
          {policy.isLoading || tiers.isLoading ? (
            <LoadingState rows={1} />
          ) : policy.error || tiers.error ? (
            <ErrorState
              error={policy.error || tiers.error}
              onRetry={() => {
                policy.refetch();
                if (can("gamification.eloTiers.view")) tiers.refetch();
              }}
            />
          ) : (
            <div className="flex min-h-14 flex-wrap content-center gap-2 py-3">
              {(tiers.data?.tiers ?? []).map((tier) => {
                const multiplier =
                  policy.data?.policy?.tierMultipliers?.[tier.code] ??
                  policy.data?.policy?.tierMultipliers?.[tier.code.toLowerCase()] ??
                  1;
                return (
                  <span
                    key={tier.id}
                    className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700"
                  >
                    {tier.name} {multiplier}×
                  </span>
                );
              })}
            </div>
          )}
        </SectionCard>
      </AdminPermission>

      <SectionCard
        title="Streak milestones"
        description="Award ELO points and a reward when a user reaches an exact streak count."
        action={
          <AdminPermission permission={"gamification.streaks.create"}>
            <Button onClick={() => open(null)}>
              <i className="fa fa-plus" />
              Add milestone
            </Button>
          </AdminPermission>
        }
      >
        {milestones.isLoading || rewards.isLoading ? (
          <LoadingState rows={4} />
        ) : milestones.error || rewards.error ? (
          <ErrorState
            error={milestones.error || rewards.error}
            onRetry={() => {
              if (can("gamification.streaks.view")) milestones.refetch();
              if (can("gamification.rewardDefinitions.view")) rewards.refetch();
            }}
          />
        ) : !milestones.data?.milestones.length ? (
          <EmptyState
            title="No milestones configured"
            body="Add a milestone such as 25 streaks, then choose its ELO points and reward."
            icon="fa-flag-checkered"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3">Milestone</th>
                  <th className="px-5 py-3">Streaks required</th>
                  <th className="px-5 py-3">ELO awarded</th>
                  <th className="px-5 py-3">Rewards</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {milestones.data.milestones.map((milestone) => (
                  <tr key={milestone.id} className="hover:bg-gray-50/70">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-950">{milestone.name}</p>
                      <StatusPill status={milestone.status} />
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-gray-800">
                      {milestone.streakCount.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      +{milestone.eloPoints.toLocaleString()} ELO
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {rewardNames(milestone)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <AdminPermission permission={"gamification.streaks.edit"}>
                        <Button variant="quiet" onClick={() => open(milestone)}>
                          Edit
                        </Button>
                      </AdminPermission>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Recent streak activity"
        description="Qualification, milestone, reset, and shield events."
        action={
          <Field label="Filter by user ID" className="w-48">
            <input
              min={1}
              type="number"
              className={fieldClass}
              value={userFilter}
              onChange={(event) => {
                setPage(1);
                setUserFilter(event.target.value);
              }}
            />
          </Field>
        }
      >
        {events.isLoading ? (
          <LoadingState rows={5} />
        ) : events.error ? (
          <ErrorState error={events.error} onRetry={() => events.refetch()} />
        ) : !events.data?.results.length ? (
          <EmptyState title="No streak events" body="No events match this filter." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-left">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-5 py-3">Event</th>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Source</th>
                    <th className="px-5 py-3">Occurred</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.data.results.map((event) => (
                    <tr key={event.id}>
                      <td className="px-5 py-4 text-sm font-semibold text-gray-900">
                        {formatLabel(event.eventType)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {event.userId ?? "System"}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {formatLabel(event.sourceType)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                        {formatDate(event.occurredAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
              <p className="text-sm text-gray-500">
                Page {events.data.meta.currentPage} of {events.data.meta.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={!events.data.meta.hasPreviousPage}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={!events.data.meta.hasNextPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </SectionCard>

      <AdminPermission
        permission={editing ? "gamification.streaks.edit" : "gamification.streaks.create"}
      >
        <Drawer
          open={editing !== undefined}
          title={editing ? "Edit milestone" : "Add milestone"}
          description="The milestone is awarded once per user, including eligible existing users."
          onClose={() => setEditing(undefined)}
        >
          <form className="space-y-5" onSubmit={submit}>
            <Field label="Milestone name" required>
              <input
                required
                minLength={2}
                maxLength={128}
                className={fieldClass}
                placeholder="25 streak milestone"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Streaks required" required>
                <input
                  required
                  min={1}
                  type="number"
                  className={fieldClass}
                  value={form.streakCount}
                  onChange={(event) => setForm({ ...form, streakCount: event.target.value })}
                />
              </Field>
              <Field label="ELO points awarded" required>
                <input
                  required
                  min={0}
                  max={1_000_000}
                  type="number"
                  className={fieldClass}
                  value={form.eloPoints}
                  onChange={(event) => setForm({ ...form, eloPoints: event.target.value })}
                />
              </Field>
            </div>
            <AdminPermission permission={"gamification.rewardDefinitions.view"}>
              <fieldset>
                <legend className="text-sm font-semibold text-gray-800">
                  Milestone rewards
                </legend>
                <p className="mt-1 text-xs text-gray-500">
                  Choose every reward that unlocks with this streak milestone.
                </p>
                <div className="mt-3 max-h-52 space-y-2 overflow-y-auto rounded-xl border border-gray-200 p-3">
                  {rewards.data?.rewards
                    .filter((reward) => reward.status !== "archived")
                    .map((reward) => (
                      <label
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-gray-50"
                        key={reward.id}
                      >
                        <span className="text-sm text-gray-700">
                          {reward.name}{" "}
                          <span className="text-xs text-gray-400">
                            · {reward.maxUses} uses
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          checked={form.rewardDefinitionIds.includes(String(reward.id))}
                          onChange={() =>
                            setForm((current) => ({
                              ...current,
                              rewardDefinitionIds: current.rewardDefinitionIds.includes(
                                String(reward.id),
                              )
                                ? current.rewardDefinitionIds.filter(
                                    (id) => id !== String(reward.id),
                                  )
                                : [...current.rewardDefinitionIds, String(reward.id)],
                            }))
                          }
                        />
                      </label>
                    ))}
                  {!rewards.data?.rewards.filter((reward) => reward.status !== "archived")
                    .length && (
                    <p className="text-sm text-gray-500">No active rewards available.</p>
                  )}
                </div>
              </fieldset>
            </AdminPermission>
            <Field label="Status">
              <select
                className={fieldClass}
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value as LifecycleStatus })
                }
              >
                {(["draft", "active", "archived"] as LifecycleStatus[]).map((status) => (
                  <option key={status} value={status}>
                    {formatLabel(status)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reason for change">
              <textarea
                rows={3}
                className={fieldClass}
                value={form.reason}
                onChange={(event) => setForm({ ...form, reason: event.target.value })}
              />
            </Field>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button type="button" variant="secondary" onClick={() => setEditing(undefined)}>
                Cancel
              </Button>
              <Button disabled={save.isLoading} type="submit">
                {save.isLoading ? "Saving…" : editing ? "Save changes" : "Add milestone"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>

      <AdminPermission permission={"gamification.streaks.create"}>
        <Drawer
          open={editingDecay}
          title="Edit ELO decay"
          description="This creates a versioned policy. It affects future missed-streak deductions only."
          onClose={() => setEditingDecay(false)}
        >
          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              saveDecay.mutate();
            }}
          >
            <Field
              label="ELO points to deduct"
              required
              hint="Use 0 to keep streak resets without an ELO deduction."
            >
              <input
                required
                min={0}
                max={1_000_000}
                step={1}
                type="number"
                className={fieldClass}
                value={decayPoints}
                onChange={(event) => setDecayPoints(event.target.value)}
              />
            </Field>
            <Field
              label="Effective from"
              required
              hint="Choose now or schedule the change for a future date."
            >
              <input
                required
                type="datetime-local"
                className={fieldClass}
                value={decayEffectiveFrom}
                onChange={(event) => setDecayEffectiveFrom(event.target.value)}
              />
            </Field>
            <Field label="Reason for change" required>
              <textarea
                required
                minLength={2}
                rows={3}
                className={fieldClass}
                value={decayReason}
                onChange={(event) => setDecayReason(event.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditingDecay(false)}
              >
                Cancel
              </Button>
              <Button disabled={saveDecay.isLoading} type="submit">
                {saveDecay.isLoading ? "Saving…" : "Save ELO decay"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>

      <AdminPermission permission={"gamification.streaks.create"}>
        <Drawer
          open={editingMultiplier}
          title="Edit active-streak multipliers"
          description="This creates a versioned earning policy. It affects future Base ELO Point awards only while a member has an active streak."
          onClose={() => setEditingMultiplier(false)}
        >
          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              saveMultiplier.mutate();
            }}
          >
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-gray-800">
                Tier multipliers
              </legend>
              <p className="text-xs text-gray-500">
                Use 1× for no bonus. For example, 1.5× turns 7 Base ELO Points into 10 points.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {(tiers.data?.tiers ?? []).map((tier) => (
                  <Field key={tier.id} label={`${tier.name} multiplier`} required>
                    <input
                      required
                      min={1}
                      max={100}
                      step={0.1}
                      type="number"
                      className={fieldClass}
                      value={tierMultipliers[tier.code] ?? "1"}
                      onChange={(event) =>
                        setTierMultipliers((current) => ({
                          ...current,
                          [tier.code]: event.target.value,
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </fieldset>
            <Field
              label="Effective from"
              required
              hint="Choose now or schedule the change for a future date."
            >
              <input
                required
                type="datetime-local"
                className={fieldClass}
                value={multiplierEffectiveFrom}
                onChange={(event) => setMultiplierEffectiveFrom(event.target.value)}
              />
            </Field>
            <Field label="Reason for change" required>
              <textarea
                required
                minLength={2}
                rows={3}
                className={fieldClass}
                value={multiplierReason}
                onChange={(event) => setMultiplierReason(event.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditingMultiplier(false)}
              >
                Cancel
              </Button>
              <Button disabled={saveMultiplier.isLoading} type="submit">
                {saveMultiplier.isLoading ? "Saving…" : "Save multipliers"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>
    </div>
  );
}

function RuleStep({
  label,
  title,
  body,
  active = false,
}: {
  label: string;
  title: string;
  body: string;
  active?: boolean;
}) {
  return (
    <div className={`p-5 ${active ? "bg-orange-50" : "bg-white"}`}>
      <p className={`text-xs font-semibold uppercase tracking-wider ${active ? "text-orange-700" : "text-gray-400"}`}>
        {label}
      </p>
      <h3 className="mt-3 text-sm font-semibold text-gray-950">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-gray-500">{body}</p>
    </div>
  );
}
